import { useAuth } from './useAuth';
import { AdminPermission, AdminRole } from '../generated/graphql';
import { debug } from 'console';

export const usePermissions = () => {
  const { user } = useAuth();

  const hasPermission = (permission: AdminPermission): boolean => {
    if (!user) {
      console.log('usePermissions - No user found for permission check:', permission);
      return false;
    }
    
    // Super admin bypasses all permission checks
    if (user.is_super_admin) {
      console.log('usePermissions - User is super admin, granting permission:', permission);
      return true;
    }
    // Ensure permissions array exists and handle potential string format
    let userPermissions: string[] = [];
    
    if (user.permissions) {
      if (Array.isArray(user.permissions)) {
        userPermissions = user.permissions;
      } else if (typeof user.permissions === 'string') {
        try {
          // Try to parse as JSON first
          const parsed = JSON.parse(user.permissions);
          if (Array.isArray(parsed)) {
            userPermissions = parsed;
          } else {
            // If not an array, try splitting by comma
            const permissionsString = user.permissions as string;
            userPermissions = permissionsString.split(',').map((p: string) => p.trim()).filter((p: string) => p);
          }
        } catch (e) {
          // If JSON parsing fails, try splitting by comma
          const permissionsString = user.permissions as string;
          userPermissions = permissionsString.split(',').map((p: string) => p.trim()).filter((p: string) => p);
        }
      }
    }
    
    console.log('usePermissions - User permissions:', userPermissions);
    console.log('usePermissions - Checking if', permission, 'is in', userPermissions);
    const hasPermission = userPermissions.includes(permission);
    console.log('usePermissions - Permission result:', hasPermission);
    
    return hasPermission;
  };

  const hasRole = (role: AdminRole): boolean => {
    if (!user) return false;
    
    // Super admin bypasses all role checks
    if (user.is_super_admin) return true;
    
    if (!user.userRole) return false;
    
    // Convert role name to enum key and compare
    const roleNameToKey: Record<string, AdminRole> = {
      'super administrator': AdminRole.SuperAdmin,
      'administrator': AdminRole.Admin,
      'manager': AdminRole.Manager,
      'operator': AdminRole.Operator,
      'nurse': AdminRole.Nurse,
      'doctor': AdminRole.Doctor,
    };
    
    const roleName = user.userRole.name.toLowerCase();
    const userRoleKey = roleNameToKey[roleName];
    return userRoleKey === role;
  };

  const canViewUsers = (): boolean => {
    return hasPermission(AdminPermission.ViewUsers);
  };

  const canCreateUsers = (): boolean => {
    return hasPermission(AdminPermission.CreateUsers);
  };

  const canUpdateUsers = (): boolean => {
    return hasPermission(AdminPermission.UpdateUsers);
  };

  const canDeleteUsers = (): boolean => {
    return hasPermission(AdminPermission.DeleteUsers);
  };

  const canManageUsers = (): boolean => {
    return canViewUsers() || canCreateUsers() || canUpdateUsers() || canDeleteUsers();
  };

  const canViewDoctors = (): boolean => {
    return hasPermission(AdminPermission.ViewDoctors);
  };

  const canCreateDoctors = (): boolean => {
    return hasPermission(AdminPermission.CreateDoctors);
  };

  const canUpdateDoctors = (): boolean => {
    return hasPermission(AdminPermission.UpdateDoctors);
  };

  const canDeleteDoctors = (): boolean => {
    return hasPermission(AdminPermission.DeleteDoctors);
  };

  const canManageDoctors = (): boolean => {
    return canViewDoctors() || canCreateDoctors() || canUpdateDoctors() || canDeleteDoctors();
  };

  const canViewClinics = (): boolean => {
    return hasPermission(AdminPermission.ViewClinics);
  };

  const canCreateClinics = (): boolean => {
    return hasPermission(AdminPermission.CreateClinics);
  };

  const canUpdateClinics = (): boolean => {
    return hasPermission(AdminPermission.UpdateClinics);
  };

  const canDeleteClinics = (): boolean => {
    return hasPermission(AdminPermission.DeleteClinics);
  };

  const canManageClinics = (): boolean => {
    return canViewClinics() || canCreateClinics() || canUpdateClinics() || canDeleteClinics();
  };

  const canViewProviders = (): boolean => {
    return hasPermission(AdminPermission.ViewProviders);
  };

  const canCreateProviders = (): boolean => {
    return hasPermission(AdminPermission.CreateProviders);
  };

  const canUpdateProviders = (): boolean => {
    return hasPermission(AdminPermission.UpdateProviders);
  };

  const canDeleteProviders = (): boolean => {
    return hasPermission(AdminPermission.DeleteProviders);
  };

  const canManageProviders = (): boolean => {
    return canViewProviders() || canCreateProviders() || canUpdateProviders() || canDeleteProviders();
  };

  const canViewAppointments = (): boolean => {
    return hasPermission(AdminPermission.ViewAppointments);
  };

  const canCreateAppointments = (): boolean => {
    return hasPermission(AdminPermission.CreateAppointments);
  };

  const canUpdateAppointments = (): boolean => {
    return hasPermission(AdminPermission.UpdateAppointments);
  };

  const canDeleteAppointments = (): boolean => {
    return hasPermission(AdminPermission.DeleteAppointments);
  };

  const canManageAppointments = (): boolean => {
    console.log('canManageAppointments', canViewAppointments(), canCreateAppointments(), canUpdateAppointments(), canDeleteAppointments());
    return canViewAppointments() || canCreateAppointments() || canUpdateAppointments() || canDeleteAppointments();
  };

  const canViewPatients = (): boolean => {
    return hasPermission(AdminPermission.ViewPatients);
  };

  const canCreatePatients = (): boolean => {
    return hasPermission(AdminPermission.CreatePatients);
  };

  const canUpdatePatients = (): boolean => {
    return hasPermission(AdminPermission.UpdatePatients);
  };

  const canDeletePatients = (): boolean => {
    return hasPermission(AdminPermission.DeletePatients);
  };

  const canManagePatients = (): boolean => {
    return canViewPatients() || canCreatePatients() || canUpdatePatients() || canDeletePatients();
  };

  const canManageAdmins = (): boolean => {
    return hasPermission(AdminPermission.ManageAdmins);
  };

  const canViewSystem = (): boolean => {
    return hasPermission(AdminPermission.ViewSystem);
  };

  const canViewLogs = (): boolean => {
    return hasPermission(AdminPermission.ViewLogs);
  };

  const canManageSettings = (): boolean => {
    return hasPermission(AdminPermission.ManageSettings);
  };

  const canViewInsurance = (): boolean => {
    return hasPermission(AdminPermission.ViewInsurance);
  };

  // Get all permissions for the current user
  const getUserPermissions = (): string[] => {
    return user?.permissions || [];
  };

  // Get user role
  const getUserRole = (): AdminRole | null => {
    if (!user?.userRole) return null;
    
    // Convert role name to enum key
    const roleNameToKey: Record<string, AdminRole> = {
      'super administrator': AdminRole.SuperAdmin,
      'administrator': AdminRole.Admin,
      'manager': AdminRole.Manager,
      'operator': AdminRole.Operator,
      'nurse': AdminRole.Nurse,
      'doctor': AdminRole.Doctor,
    };
    
    const roleName = user.userRole.name.toLowerCase();
    return roleNameToKey[roleName] || null;
  };

  // Check if user is super admin
  const isSuperAdmin = (): boolean => {
    return user?.is_super_admin || false;
  };

  return {
    hasPermission,
    hasRole,
    canViewUsers,
    canCreateUsers,
    canUpdateUsers,
    canDeleteUsers,
    canManageUsers,
    canViewDoctors,
    canCreateDoctors,
    canUpdateDoctors,
    canDeleteDoctors,
    canManageDoctors,
    canViewClinics,
    canCreateClinics,
    canUpdateClinics,
    canDeleteClinics,
    canManageClinics,
    canViewProviders,
    canCreateProviders,
    canUpdateProviders,
    canDeleteProviders,
    canManageProviders,
    canViewAppointments,
    canCreateAppointments,
    canUpdateAppointments,
    canDeleteAppointments,
    canManageAppointments,
    canViewPatients,
    canCreatePatients,
    canUpdatePatients,
    canDeletePatients,
    canManagePatients,
    canManageAdmins,
    canViewSystem,
    canViewLogs,
    canManageSettings,
    canViewInsurance,
    getUserPermissions,
    getUserRole,
    isSuperAdmin,
  };
}; 